// Sprint 0078 — Notifications + WhatsApp share API client.
// Backend: see backend/app/api/routers/notifications.py

import apiClient from '@/shared/api/client'

export type NotificationSeverity = 'INFO' | 'WARNING' | 'CRITICAL'
export type NotificationChannel = 'IN_APP' | 'WHATSAPP' | 'EMAIL' | 'SMS'

export interface NotificationRow {
  id: string
  event_type: string
  title: string
  body: string | null
  severity: NotificationSeverity
  entity_type: string | null
  entity_id: string | null
  channel: NotificationChannel
  metadata_json: Record<string, unknown> | null
  read_at: string | null
  created_at: string
}

export const notificationsApi = {
  list: async (params?: { unread_only?: boolean; limit?: number }) => {
    const r = await apiClient.get<NotificationRow[]>('/notifications', { params })
    return r.data
  },

  unreadCount: async () => {
    const r = await apiClient.get<{ unread: number }>('/notifications/unread-count')
    return r.data.unread
  },

  markRead: async (id: string) => {
    await apiClient.post(`/notifications/${id}/mark-read`)
  },

  markAllRead: async () => {
    const r = await apiClient.post<{ marked: number }>('/notifications/mark-all-read')
    return r.data.marked
  },
}

// ── WhatsApp share API ──────────────────────────────────────────────────────

export interface WhatsAppLinkRequest {
  title: string
  body: string
  entity_type?: string | null
  entity_id?: string | null
}

export interface WhatsAppLinkResponse {
  url: string
  group_invite_link: string | null
}

export interface WhatsAppConfig {
  id: string
  provider: string
  group_phone: string | null
  group_invite_link: string | null
  is_active: boolean
}

export const whatsappApi = {
  generateLink: async (body: WhatsAppLinkRequest) => {
    const r = await apiClient.post<WhatsAppLinkResponse>('/whatsapp/link', body)
    return r.data
  },

  getConfig: async () => {
    const r = await apiClient.get<WhatsAppConfig | null>('/whatsapp/config')
    return r.data
  },

  updateConfig: async (body: {
    group_phone?: string | null
    group_invite_link?: string | null
    provider?: string | null
  }) => {
    const r = await apiClient.put<WhatsAppConfig>('/whatsapp/config', body)
    return r.data
  },
}

// Helper: open the wa.me link in a new tab.
// In the click-to-chat era this is the user's manual send step.
// When we migrate to Cloud API, this helper becomes a no-op call to
// the API endpoint that auto-sends.
export function openWhatsAppShare(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}
